import React, { useCallback, useMemo, useRef, useState } from "react";
import { CloudUpload } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { ColDef, GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { Feature } from "geojson";
import Papa from "papaparse";

import { isExcelFile, xlsxToJson } from "fond/utils/xlsx";
import { AgGrid, Dropzone, Modal } from "fond/widgets";

interface CsvPreviewModalProps {
  /**
   * Flag that controls if the modal is open
   */
  open: boolean;
  /**
   * Callback function fired when the modal closes
   */
  onClose: () => void;
  /**
   * Callback function fired when OK button is clicked
   * @param ids - Array of feature IDs that were matched
   * @param action - Action to perform on the selected IDs
   */
  onConfirm: (ids: string[], action: "prioritize" | "defer") => void;
  /**
   * Array of features to match against
   */
  features: Feature[];
}

const CsvPreviewModal: React.FC<CsvPreviewModalProps> = ({ open, onClose, onConfirm, features }) => {
  const gridApiRef = useRef<GridApi | null>(null);
  const [matchedFeatures, setMatchedFeatures] = useState<Feature[]>([]);
  const [unmatchedIds, setUnmatchedIds] = useState<string[]>([]);
  const [matchStats, setMatchStats] = useState<{ total: number; matched: number; unmatched: number }>({ total: 0, matched: 0, unmatched: 0 });
  const [error, setError] = useState<{ title: string; message: string } | undefined>();
  const [activeTab, setActiveTab] = useState(0);

  // Generate column definitions from matched features
  const columns: ColDef[] = useMemo(() => {
    if (matchedFeatures.length === 0) return [];

    const properties = matchedFeatures[0].properties || {};
    const propertyKeys = Object.keys(properties);
    const keys = propertyKeys.includes("id") ? propertyKeys : ["id", ...propertyKeys];

    // We store the feature ID in a hidden column for easy access when confirming selection
    const featureIdentifier: ColDef = {
      field: "featureIdentifier",
      hide: true,
    };

    const dataColumns = keys.map((key) => ({
      headerName: key,
      field: key,
      hide: key === "id",
    }));

    return [featureIdentifier, ...dataColumns];
  }, [matchedFeatures]);

  // Convert matched features to row data
  const rowData = useMemo(() => {
    return matchedFeatures.map((feature) => ({ featureIdentifier: feature.id, ...feature.properties }));
  }, [matchedFeatures]);

  // Columns for unmatched IDs grid
  const unmatchedColumns: ColDef[] = useMemo(
    () => [
      {
        headerName: "Unmatched ID",
        field: "id",
        flex: 1,
      },
    ],
    []
  );

  // Row data for unmatched IDs
  const unmatchedRowData = useMemo(() => {
    return unmatchedIds.map((id, index) => ({ id, rowId: index }));
  }, [unmatchedIds]);

  const gridOptions: GridOptions = useMemo(
    () => ({
      animateRows: true,
      rowGroupPanelShow: "never",
      sideBar: false,
      paginationAutoPageSize: true,
      pagination: true,
      suppressMovableColumns: true,
      suppressRowClickSelection: true,
      autoSizeStrategy: {
        type: "fitGridWidth",
        defaultMinWidth: 100,
      },
      getRowId: (params) => String(params.data.featureIdentifier),
    }),
    []
  );

  // Handle grid ready event to select all rows by default
  const handleGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
  }, []);

  // Reset all state and close modal
  const handleClose = useCallback(() => {
    setMatchedFeatures([]);
    setUnmatchedIds([]);
    setMatchStats({ total: 0, matched: 0, unmatched: 0 });
    setError(undefined);
    setActiveTab(0);
    onClose();
  }, [onClose]);

  const handleConfirm = (action: "prioritize" | "defer") => () => {
    if (!gridApiRef.current) return;

    const allRowIds: string[] = [];
    gridApiRef.current.forEachNode((node) => {
      if (node.id !== undefined) {
        allRowIds.push(node.id);
      }
    });

    onConfirm(allRowIds, action);
    handleClose();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderActions = () => {
    // No file uploaded yet
    if (matchedFeatures.length === 0 && unmatchedIds.length === 0) {
      return (
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      );
    }

    // File uploaded but no matches
    if (matchedFeatures.length === 0) {
      return (
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
      );
    }

    // Has matches - show action buttons
    return (
      <>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleConfirm("defer")} variant="contained" color="error">
          Defer
        </Button>
        <Button onClick={handleConfirm("prioritize")} variant="contained" color="success">
          Prioritize
        </Button>
      </>
    );
  };

  /**
   * Matches CSV/Excel IDs against feature properties
   * Returns array of matched features
   */
  const matchFeaturesFromIds = useCallback(
    (csvIds: string[]): Feature[] => {
      const idSet = new Set(csvIds);

      return features.filter((feature) => {
        if (!feature.properties) return false;

        // Check if any property value matches any of the CSV IDs
        const propertyValues = Object.values(feature.properties).map((val) => String(val));
        return propertyValues.some((value) => idSet.has(value));
      });
    },
    [features]
  );

  const handleOnDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    try {
      let csvRows: Record<string, unknown>[] = [];

      // Check if file is Excel
      if (isExcelFile(file)) {
        // Convert Excel to JSON
        const result = await xlsxToJson(file);

        if (result.data.length === 0) {
          setError({
            title: "Invalid Excel file",
            message: "The Excel file appears to be empty or invalid.",
          });
          return;
        }

        csvRows = result.data;
      } else {
        // Handle CSV files using papaparse
        const text = await file.text();

        const parseResult = Papa.parse<Record<string, unknown>>(text, {
          header: false, // Don't treat first row as header since we don't know the structure
          skipEmptyLines: true,
        });

        const errorsWeCareAbout = parseResult.errors.filter((err: Papa.ParseError) => err.code !== "UndetectableDelimiter");
        if (errorsWeCareAbout.length > 0) {
          const errorMessages = errorsWeCareAbout.map((err: Papa.ParseError) => err.message).join(", ");
          setError({
            title: "CSV Parsing Errors",
            message: errorMessages,
          });
          return;
        }

        if (parseResult.data.length === 0) {
          setError({
            title: "Invalid CSV",
            message: "The CSV file appears to be empty or has no data rows.",
          });
          return;
        }

        csvRows = parseResult.data;
      }

      // Extract IDs from first column of CSV
      const csvIds = csvRows
        .map((row) => {
          // Get value from first column
          const firstColumnValue = Object.values(row)[0];
          return String(firstColumnValue);
        })
        .filter((id) => id && id.length > 0);

      if (csvIds.length === 0) {
        setError({
          title: "No IDs found",
          message: "Could not find any ID values in the uploaded file. Please ensure the file contains an ID column.",
        });
        return;
      }

      // Match CSV IDs against features
      const matched = matchFeaturesFromIds(csvIds);

      // Create a set of matched property values to identify unmatched IDs
      const matchedValues = new Set<string>();
      matched.forEach((feature) => {
        if (feature.properties) {
          Object.values(feature.properties).forEach((val) => {
            matchedValues.add(String(val));
          });
        }
      });

      // Find IDs that didn't match
      let unmatchedIdsList = csvIds.filter((id) => !matchedValues.has(id));

      // Detect if first row is likely a header by checking if it's unmatched
      let adjustedTotal = csvIds.length;
      let adjustedUnmatched = csvIds.length - matched.length;

      if (csvIds.length > 0) {
        // Check if first ID matches any feature
        const firstIdMatched = features.some((feature) => {
          if (!feature.properties) return false;
          const propertyValues = Object.values(feature.properties).map((val) => String(val));
          return propertyValues.includes(csvIds[0]);
        });

        // If first row is unmatched and there's more than one row, assume it's a header
        if (!firstIdMatched && csvIds.length > 1) {
          adjustedTotal = csvIds.length - 1;
          adjustedUnmatched -= 1;
          // Remove first row from unmatched list if it's a header
          unmatchedIdsList = unmatchedIdsList.slice(1);
        }
      }

      // Update statistics
      setMatchStats({
        total: adjustedTotal,
        matched: matched.length,
        unmatched: adjustedUnmatched,
      });

      setMatchedFeatures(matched);
      setUnmatchedIds(unmatchedIdsList);
      setError(undefined);
    } catch (err) {
      setError({
        title: "Failed to parse file",
        message: "There was an error reading the file. Please ensure it is a valid CSV or Excel format.",
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      variant="primary"
      header={matchedFeatures.length > 0 ? "Matched Features" : "Upload"}
      headerIcon={matchedFeatures.length === 0 ? <CloudUpload /> : undefined}
      PaperProps={{ style: { minHeight: "80vh" } }}
      DialogContentProps={{ sx: { display: "flex" } }}
      content={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1, width: "100%" }}>
          {matchedFeatures.length === 0 && unmatchedIds.length === 0 ? (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                To get started, please upload a CSV or Excel file containing the IDs of the features you want to defer or prioritize. The first column
                of the file should contain the IDs that correspond to properties on the features.
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <AlertTitle>{error.title}</AlertTitle>
                  {error.message}
                </Alert>
              )}
              <Dropzone
                onDropAccepted={handleOnDrop}
                acceptedFiles={{
                  "text/csv": [".csv"],
                  "application/vnd.ms-excel": [".csv", ".xls"],
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                }}
                title="CSV/Excel Upload"
                message="Drag and drop CSV or Excel file or click to open explorer."
                multiple={false}
              />
            </>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
              {matchStats.matched === 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <AlertTitle>No Matches Found</AlertTitle>
                  None of the {matchStats.total} IDs from the file matched any features. Please check the Unmatched tab to see all IDs.
                </Alert>
              )}
              {matchStats.matched > 0 && matchStats.unmatched > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Partial Match</AlertTitle>
                  {matchStats.matched} of {matchStats.total} IDs matched ({matchStats.unmatched} unmatched). Please review the matches below and
                  select an action.
                </Alert>
              )}
              {matchStats.matched > 0 && matchStats.unmatched === 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  All {matchStats.matched} features matched from the uploaded file. Please review the matches below and select an action.
                </Alert>
              )}
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tab label={`Matched (${matchStats.matched})`} />
                <Tab label={`Unmatched (${matchStats.unmatched})`} disabled={matchStats.unmatched === 0} />
              </Tabs>
              <Box sx={{ flexGrow: 1, mt: 2 }}>
                {activeTab === 0 && (
                  <AgGrid
                    rowData={rowData}
                    columnDefs={columns}
                    gridOptions={gridOptions}
                    variant="outlined"
                    size="compact"
                    onGridReady={handleGridReady}
                  />
                )}
                {activeTab === 1 && (
                  <AgGrid
                    rowData={unmatchedRowData}
                    columnDefs={unmatchedColumns}
                    gridOptions={{
                      ...gridOptions,
                      rowSelection: undefined,
                      getRowId: (params) => String(params.data.rowId),
                    }}
                    variant="outlined"
                    size="compact"
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      }
      actions={renderActions()}
    />
  );
};

export default CsvPreviewModal;
